import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { ArrowRight, Target, Users, Lightbulb, Shield, Award, Globe } from 'lucide-react';
import ScheduleMeetingForm from '../components/forms/ScheduleMeetingForm';

const About = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const values = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Results-Driven',
      description: 'We focus on delivering measurable outcomes that directly impact your bottom line.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Client-Centric',
      description: 'Your success is our priority. We partner with you to understand your unique challenges.',
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: 'Innovation',
      description: 'We stay ahead of technology trends to bring you cutting-edge solutions.',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Integrity',
      description: 'Transparency and honesty guide every interaction and decision we make.',
    },
  ];

  const team = [
    {
      name: 'Leadership Team',
      role: 'Executive Management',
      description: 'Experienced leaders driving digital transformation',
    },
    {
      name: 'Solution Architects',
      role: 'Technical Excellence',
      description: 'Deep expertise in cloud, data, and AI technologies',
    },
    {
      name: 'Project Managers',
      role: 'Delivery Excellence',
      description: 'Scrum Masters and PMs ensuring on-time delivery',
    },
    {
      name: 'Engineers',
      role: 'Implementation',
      description: 'Full-stack developers, data engineers, and ML specialists',
    },
  ];

  const stats = [
    { value: 'Canadian', label: 'IT Consulting Firm' },
    { value: 'Analytics', label: 'AI & Digital Focus' },
    { value: 'Industry', label: 'Grade Solutions' },
    { value: 'Partner', label: 'Strategic Approach' },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 grain-texture" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-blue-600 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              About Us
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Helping Your Business Model Thrive
            </h1>
            <p className="text-lg md:text-xl text-slate-500 leading-relaxed">
              Zendata Solutions is a Canadian IT consulting organization with a strong focus on Analytics, AI & Digital transformation solutions. We deliver high quality Analytics architectures along with Industry grade, cloud strategy and business transformation practices.
            </p>
          </div>
        </div>
      </section>

      {/* Value Strip */}
      <section className="py-6 bg-slate-900" data-testid="about-value-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-white text-lg md:text-xl font-medium tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
            Data Driven <span className="text-slate-500 mx-3">|</span> Transformative <span className="text-slate-500 mx-3">|</span> Cloud Enabled Solutions
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-b border-slate-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center" data-testid={`stat-${idx}`}>
                <p className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {stat.value}
                </p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Our Mission
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Digital Transformation Experts
              </h2>
              <div className="space-y-4 text-slate-500 leading-relaxed">
                <p>
                  In today's fast-paced environment, our industry-based solutions will help you simplify your business processes leveraging meaningful Digital Transformation Initiatives.
                </p>
                <p>
                  We believe in working with you as part of your team to ensure quality projects and products are delivered on time. Our approach is built on collaboration, innovation, and a deep commitment to your success.
                </p>
                <p>
                  From Cloud Services and Application Development to Data Analytics, AI/ML, and RPA, we provide comprehensive solutions tailored to your specific business needs.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center">
                <Globe className="w-24 h-24 text-slate-300" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              What We Stand For
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card 
                key={idx}
                className="p-8 bg-white border border-slate-100 rounded-xl text-center"
                data-testid={`value-card-${idx}`}
              >
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {value.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Team
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Talented Professionals
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, idx) => (
              <div key={idx} className="text-center" data-testid={`team-member-${idx}`}>
                <div className="aspect-square rounded-2xl bg-slate-100 mb-4 flex items-center justify-center">
                  <Award className="w-16 h-16 text-slate-300" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  {member.name}
                </h3>
                <p className="text-sm text-blue-600 mb-2">{member.role}</p>
                <p className="text-sm text-slate-500">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Quote */}
      <section className="py-16 bg-slate-900" data-testid="approach-quote">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <blockquote className="text-2xl md:text-3xl font-medium text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            "You Miss 100% Of The Shots You Don't Take."
          </blockquote>
          <cite className="text-slate-400">— Wayne Gretzky</cite>
          <p className="text-slate-300 mt-6 max-w-2xl mx-auto">
            We believe we can alleviate concerns by keeping costs low, working with you as part of your team to ensure quality projects and products are delivered on time!
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Start Your Transformation Journey?
          </h2>
          <p className="text-lg text-slate-500 mb-8 max-w-2xl mx-auto">
            Request a free discovery session and speak to one of our Transformation Experts.
          </p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-base btn-click"
                data-testid="about-cta-btn"
              >
                Let's Chat
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                  Schedule a Meeting
                </DialogTitle>
                <DialogDescription>Fill out the form and we'll get back to you within 24 hours.</DialogDescription>
              </DialogHeader>
              <ScheduleMeetingForm onSuccess={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default About;
